/*
  Product reviews (sections/product-reviews.liquid) and the store-wide list on the reviews page
  (sections/review-all-reviews.liquid, root has data-scope="all": every review, each with its
  product; no write/ask dialogs, no Questions tab).
  Contract: replacement_parts_backend/REVIEWS-CONTRACT.md.

  The section server-renders the summary and the first reviews from Judge.me's product metafield.
  This script loads the full data from the homsee-bulk-order backend through the App Proxy:
    GET  {endpoint}/reviews?product_id=…   list, summary, mentions, photos (filters/sort/search/page)
                                          (?scope=all instead of product_id on the reviews page)
    POST {endpoint}/reviews/vote           "Helpful" vote
    POST {endpoint}/reviews/uploads        staged upload targets for review photos
    POST {endpoint}/reviews/submit         new review (forwarded to Judge.me, moderated there)
    GET  {endpoint}/questions?product_id=… published questions
    POST {endpoint}/questions              new question (then the native contact form emails staff)
  If the first GET fails, the server-rendered content stays and backend-only controls stay hidden.
*/
(() => {
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const OK_TYPES = /^image\/(jpeg|png|webp|heic|heif)$/i;
  const EXT_TYPES = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', heic: 'image/heic', heif: 'image/heif' };
  const MAX_PHOTOS = 5;
  const MAX_BYTES = 10 * 1024 * 1024;
  const VISIBLE_MENTIONS = 6;
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const MSG = {
    rating: 'Please choose a star rating.',
    body: 'Please write a few words about the product.',
    question: 'Please enter your question (at least 5 characters).',
    name: 'Please enter your name.',
    email: 'Please enter a valid email address.',
    fileType: 'Only JPG, PNG, WEBP or HEIC images',
    fileSize: 'Each photo must be 10 MB or smaller',
    tooMany: 'You can add up to 5 photos',
    uploading: 'Uploading photos…',
    saving: 'Submitting…',
    error: 'Something went wrong. Please try again.',
    busy: 'Too many attempts. Please try again later.',
    noMatch: 'No reviews match these filters.',
    noQuestions: 'No questions yet. Have a question about this product? Ask us.',
    unavailable: 'Reviews are unavailable right now. Please try again later.'
  };
  const ICON = {
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>',
    thumb: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" aria-hidden="true"><path d="M7 10v11H3V10h4zM7 10l4.5-7.5c1.3 0 2.5 1 2.3 2.4L13 9h6.3c1.3 0 2.2 1.2 1.9 2.4l-1.9 7.6c-.2.9-1 1.5-1.9 1.5H7"/></svg>',
    x: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>'
  };

  const uuid = () => {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    const b = crypto.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 15) | 64; b[8] = (b[8] & 63) | 128;
    const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  };
  const voterId = () => {
    let id = '';
    try { id = localStorage.getItem('homsee_review_voter') || ''; } catch (e) { /* storage blocked */ }
    if (!UUID.test(id)) {
      id = uuid();
      try { localStorage.setItem('homsee_review_voter', id); } catch (e) { /* storage blocked */ }
    }
    return id;
  };
  const fmtDate = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
    return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
  };
  const isHttps = (u) => typeof u === 'string' && /^https:\/\/[^\s"<>]+$/.test(u);
  const make = (tag, cls, text) => {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const stars = (rating, small) => {
    const wrap = make('span', 'prv-stars' + (small ? ' is-sm' : ''));
    const value = Math.max(0, Math.min(5, Number(rating) || 0));
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', `${small ? value : value.toFixed(2)} out of 5 stars`);
    const base = make('span', 'prv-stars-base', '★★★★★');
    const fill = make('span', 'prv-stars-fill', '★★★★★');
    base.setAttribute('aria-hidden', 'true');
    fill.setAttribute('aria-hidden', 'true');
    fill.style.width = `${(value / 5) * 100}%`;
    wrap.append(base, fill);
    return wrap;
  };

  class Reviews {
    constructor(root) {
      this.root = root;
      this.scope = root.closest('.shopify-section') || document;
      this.endpoint = root.dataset.endpoint || '';
      this.productId = root.dataset.productId || '';
      this.all = root.dataset.scope === 'all';
      this.voter = voterId();
      this.state = { page: 1, sort: 'relevant', rating: '', photos: false, q: '', mention: '' };
      this.online = false;
      this.seq = 0;
      this.questionsLoaded = false;
      this.gallery = [];
      this.galleryIndex = 0;
      this.q = (sel) => root.querySelector(sel);
      this.bindStatic();
      this.bindDialogs();
      this.load();
    }

    url(path, params) {
      if (!/^\/apps\/[a-zA-Z0-9/_-]+$/.test(this.endpoint)) throw new Error('configuration');
      const query = params ? '?' + new URLSearchParams(params).toString() : '';
      return this.endpoint + path + query;
    }

    async getJSON(path, params) {
      const response = await fetch(this.url(path, params), { headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error('http ' + response.status);
      return response.json();
    }

    async postJSON(path, body, timeout) {
      const response = await fetch(this.url(path), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(timeout || 20000)
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        const err = new Error('http ' + response.status);
        err.status = response.status;
        throw err;
      }
      return result;
    }

    /* ---------- list ---------- */

    params() {
      const p = { ...(this.all ? { scope: 'all' } : { product_id: this.productId }), page: String(this.state.page), sort: this.state.sort, voter: this.voter };
      if (this.state.rating) p.rating = this.state.rating;
      if (this.state.photos) p.photos = '1';
      if (this.state.q) p.q = this.state.q;
      if (this.state.mention) p.mention = this.state.mention;
      return p;
    }

    async load(scroll) {
      const seq = ++this.seq;
      const list = this.q('[data-prv-list]');
      list.classList.add('is-loading');
      try {
        const data = await this.getJSON('/reviews', this.params());
        if (seq !== this.seq) return;
        this.goOnline(data);
        this.renderList(data);
        this.renderPages(data.page || 1, data.pages || 1);
        this.renderNote(data.total || 0);
        if (scroll) this.scrollToList();
      } catch (e) {
        if (seq !== this.seq) return;
        // Keep the server-rendered reviews; only backend-driven controls stay hidden.
        if (!this.online) {
          this.root.classList.add('is-offline');
          const more = this.q('[data-prv-ssr-more]');
          if (more) more.hidden = false;
          // The store-wide list has nothing server-rendered: say so instead of "Loading…".
          const loading = this.q('[data-prv-loading]');
          if (loading) loading.textContent = MSG.unavailable;
        }
      } finally {
        if (seq === this.seq) list.classList.remove('is-loading');
      }
    }

    goOnline(data) {
      const first = !this.online;
      this.online = true;
      this.root.classList.remove('is-offline');
      const summary = data.summary || {};
      const count = Number(summary.count) || 0;
      if (first) {
        const more = this.q('[data-prv-ssr-more]');
        if (more) more.remove();
        this.renderSummary(summary);
        this.renderMentions(data.mentions || []);
        this.renderMedia(data.media || []);
        this.q('[data-prv-mentions-panel]').hidden = count === 0;
        this.q('[data-prv-filters]').hidden = count === 0;
        this.q('[data-prv-bar]').hidden = false;
        const qTab = this.q('[data-prv-tab="questions"]');
        if (qTab && this.scope.querySelector('[data-prv-dialog="question"]')) qTab.hidden = false;
        const empty = this.q('[data-prv-empty]');
        if (empty) empty.hidden = count > 0;
      }
      this.q('[data-prv-tab-count]').textContent = count;
      const qCount = this.q('[data-prv-q-count]');
      if (qCount && typeof data.questions_count === 'number' && !this.questionsLoaded) qCount.textContent = data.questions_count;
    }

    renderSummary(summary) {
      const count = Number(summary.count) || 0;
      const avg = Number(summary.average) || 0;
      this.q('[data-prv-summary]').hidden = count === 0;
      this.q('[data-prv-avg]').textContent = avg.toFixed(2);
      const starsEl = this.q('[data-prv-avg-stars]');
      starsEl.setAttribute('aria-label', `${avg.toFixed(2)} out of 5 stars`);
      starsEl.querySelector('.prv-stars-fill').style.width = `${(avg / 5) * 100}%`;
      this.q('[data-prv-count]').textContent = count;
      const countLabel = this.q('[data-prv-count]').parentElement;
      if (countLabel) countLabel.lastChild.textContent = count === 1 ? ' review' : ' reviews';
      const hist = this.q('[data-prv-hist]');
      hist.textContent = '';
      const histogram = summary.histogram || {};
      [5, 4, 3, 2, 1].forEach((rating) => {
        const n = Number(histogram[rating]) || 0;
        const li = make('li');
        const row = make('button', 'prv-hist-row');
        row.type = 'button';
        row.disabled = n === 0;
        row.setAttribute('aria-label', `Show ${rating}-star reviews (${n})`);
        row.append(make('span', '', `${rating} ${rating === 1 ? 'star' : 'stars'}`));
        const bar = make('span', 'prv-hist-bar');
        const fill = make('span');
        fill.style.width = count ? `${Math.round((n / count) * 100)}%` : '0%';
        bar.append(fill);
        row.append(bar, make('em', '', String(n)));
        row.addEventListener('click', () => {
          this.setFilter({ rating: String(rating) });
          this.q('[data-prv-rating]').value = String(rating);
        });
        li.append(row);
        hist.append(li);
      });
    }

    renderMentions(mentions) {
      const chips = this.q('[data-prv-chips]');
      const wrap = this.q('[data-prv-mentions-wrap]');
      chips.textContent = '';
      wrap.hidden = !mentions.length;
      mentions.forEach((m) => {
        const li = make('li');
        const chip = make('button', 'prv-chip');
        chip.type = 'button';
        chip.dataset.mention = m.id;
        chip.setAttribute('aria-pressed', 'false');
        chip.append(document.createTextNode(m.label + ' '), make('b', '', String(m.count)));
        chip.addEventListener('click', () => {
          const on = this.state.mention !== m.id;
          this.setFilter({ mention: on ? m.id : '' });
        });
        li.append(chip);
        chips.append(li);
      });
      const more = this.q('[data-prv-more]');
      more.hidden = mentions.length <= VISIBLE_MENTIONS;
      chips.classList.toggle('is-collapsed', mentions.length > VISIBLE_MENTIONS);
    }

    syncMentionChips() {
      this.root.querySelectorAll('[data-mention]').forEach((chip) => {
        chip.setAttribute('aria-pressed', String(chip.dataset.mention === this.state.mention));
      });
    }

    renderMedia(media) {
      const box = this.q('[data-prv-media]');
      const track = this.q('[data-prv-media-track]');
      const items = media.filter((m) => isHttps(m.thumb) && isHttps(m.full));
      track.textContent = '';
      items.forEach((m, i) => {
        const btn = make('button', 'prv-media-item');
        btn.type = 'button';
        btn.dataset.prvZoom = '';
        btn.dataset.full = m.full;
        btn.setAttribute('aria-label', `Open customer photo ${i + 1} of ${items.length}`);
        const img = make('img');
        img.src = m.thumb;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 160;
        img.height = 160;
        btn.append(img);
        track.append(btn);
      });
      box.hidden = !items.length;
    }

    renderList(data) {
      const list = this.q('[data-prv-list]');
      list.textContent = '';
      const reviews = Array.isArray(data.reviews) ? data.reviews : [];
      if (!reviews.length) {
        const total = data.summary && Number(data.summary.count);
        if (total) {
          const li = make('li', 'prv-empty');
          li.append(make('p', '', MSG.noMatch));
          const clear = make('button', 'prv-btn prv-btn-outline', 'Clear filters');
          clear.type = 'button';
          clear.addEventListener('click', () => this.clearFilters());
          li.append(clear);
          list.append(li);
        }
        return;
      }
      reviews.forEach((r) => list.append(this.renderItem(r)));
    }

    renderItem(r) {
      const li = make('li', 'prv-item');
      if (this.all && r.product && r.product.title) li.append(this.renderProduct(r.product));
      li.append(stars(r.rating, true));
      const head = make('div', 'prv-item-head');
      head.append(make('span', 'prv-author', r.author || 'Customer'));
      if (r.verified) {
        const badge = make('span', 'prv-badge');
        badge.innerHTML = ICON.check;
        badge.append(document.createTextNode('Verified Buyer'));
        head.append(badge);
      }
      li.append(head);
      if (r.title) li.append(make('p', 'prv-item-title', r.title));
      li.append(make('p', 'prv-body', r.body || ''));
      const photos = (r.photos || []).filter((p) => isHttps(p.thumb) && isHttps(p.full));
      if (photos.length) {
        const ul = make('ul', 'prv-photos');
        photos.forEach((p, i) => {
          const item = make('li');
          const btn = make('button', 'prv-photo');
          btn.type = 'button';
          btn.dataset.prvZoom = '';
          btn.dataset.full = p.full;
          btn.setAttribute('aria-label', `Open photo ${i + 1} from ${r.author || 'this review'}`);
          const img = make('img');
          img.src = p.thumb;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 92;
          img.height = 92;
          btn.append(img);
          item.append(btn);
          ul.append(item);
        });
        li.append(ul);
      }
      const foot = make('div', 'prv-item-foot');
      const helpful = make('button', 'prv-helpful');
      helpful.type = 'button';
      const setHelpful = (count, voted) => {
        helpful.innerHTML = ICON.thumb;
        helpful.append(document.createTextNode(`Helpful (${count})`));
        helpful.setAttribute('aria-pressed', String(!!voted));
        helpful.disabled = !!voted;
        helpful.setAttribute('aria-label', voted ? `You found this helpful (${count})` : `Mark as helpful (${count})`);
      };
      setHelpful(Number(r.helpful) || 0, r.voted);
      helpful.addEventListener('click', async () => {
        if (helpful.disabled) return;
        helpful.disabled = true;
        try {
          const res = await this.postJSON('/reviews/vote', { review_id: r.id, voter: this.voter }, 10000);
          setHelpful(Number(res.helpful) || (Number(r.helpful) || 0) + 1, true);
        } catch (e) {
          helpful.disabled = false;
        }
      });
      const time = make('time', '', fmtDate(r.date));
      if (r.date) time.dateTime = r.date;
      foot.append(helpful, time);
      li.append(foot);
      return li;
    }

    // Store-wide list: which product the review is for (linked when it's on sale).
    renderProduct(product) {
      const url = /^\/products\/[a-z0-9-]+$/i.test(product.url || '') ? product.url : '';
      const box = make(url ? 'a' : 'div', 'prv-product');
      if (url) box.href = url;
      if (isHttps(product.image)) {
        const img = make('img');
        img.src = product.image;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 48;
        img.height = 48;
        box.append(img);
      }
      box.append(make('span', 'prv-product-title', product.title));
      return box;
    }

    renderPages(page, pages) {
      const nav = this.q('[data-prv-pages]');
      nav.textContent = '';
      nav.hidden = pages <= 1;
      if (pages <= 1) return;
      const add = (label, target, opts = {}) => {
        const btn = make('button', 'prv-page', label);
        btn.type = 'button';
        if (opts.aria) btn.setAttribute('aria-label', opts.aria);
        if (target === page && !opts.nav) btn.setAttribute('aria-current', 'page');
        btn.disabled = !!opts.disabled;
        btn.addEventListener('click', () => { this.state.page = target; this.load(true); });
        nav.append(btn);
      };
      add('‹', page - 1, { nav: true, disabled: page <= 1, aria: 'Previous page' });
      const shown = new Set([1, pages, page - 1, page, page + 1].filter((n) => n >= 1 && n <= pages));
      let last = 0;
      [...shown].sort((a, b) => a - b).forEach((n) => {
        if (n - last > 1) nav.append(make('span', 'prv-page-gap', '…'));
        add(String(n), n, { aria: `Page ${n}` });
        last = n;
      });
      add('›', page + 1, { nav: true, disabled: page >= pages, aria: 'Next page' });
    }

    renderNote(total) {
      const note = this.q('[data-prv-note]');
      const active = this.state.rating || this.state.photos || this.state.q || this.state.mention;
      note.textContent = '';
      note.hidden = !active;
      if (!active) return;
      note.append(document.createTextNode(`${total} ${total === 1 ? 'review' : 'reviews'} match. `));
      const clear = make('button', '', 'Clear filters');
      clear.type = 'button';
      clear.addEventListener('click', () => this.clearFilters());
      note.append(clear);
    }

    setFilter(change) {
      Object.assign(this.state, change, { page: 1 });
      this.syncMentionChips();
      this.load(true);
    }

    clearFilters() {
      this.state = { ...this.state, page: 1, rating: '', photos: false, q: '', mention: '' };
      this.q('[data-prv-rating]').value = '';
      this.q('[data-prv-photos]').setAttribute('aria-pressed', 'false');
      this.q('[data-prv-q]').value = '';
      this.syncMentionChips();
      this.load(true);
    }

    scrollToList() {
      const bar = this.q('[data-prv-bar]');
      const top = bar.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight) bar.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /* ---------- questions ---------- */

    async loadQuestions() {
      const list = this.q('[data-prv-qlist]');
      list.classList.add('is-loading');
      try {
        const data = await this.getJSON('/questions', { product_id: this.productId });
        this.questionsLoaded = true;
        const questions = Array.isArray(data.questions) ? data.questions : [];
        this.q('[data-prv-q-count]').textContent = Number(data.count) || questions.length;
        list.textContent = '';
        if (!questions.length) {
          const li = make('li', 'prv-empty');
          li.append(make('p', '', MSG.noQuestions));
          const ask = make('button', 'prv-btn prv-btn-outline', 'Ask a question');
          ask.type = 'button';
          ask.addEventListener('click', () => this.openDialog('question'));
          li.append(ask);
          list.append(li);
          return;
        }
        questions.forEach((item) => {
          const li = make('li', 'prv-q');
          li.append(make('p', 'prv-q-text', 'Q: ' + (item.question || '')));
          li.append(make('p', 'prv-q-meta', [item.asker, fmtDate(item.date)].filter(Boolean).join(' · ')));
          if (item.answer) {
            const a = make('p', 'prv-a', 'A: ' + item.answer);
            a.append(make('span', 'prv-a-meta', [item.answered_by || 'Homsee', fmtDate(item.answered_date)].filter(Boolean).join(' · ')));
            li.append(a);
          }
          list.append(li);
        });
      } catch (e) {
        list.textContent = '';
        const li = make('li', 'prv-empty');
        li.append(make('p', '', 'Questions are unavailable right now. Please try again later.'));
        list.append(li);
      } finally {
        list.classList.remove('is-loading');
      }
    }

    selectTab(name, focus) {
      this.root.querySelectorAll('[data-prv-tab]').forEach((tab) => {
        const on = tab.dataset.prvTab === name;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
        if (on && focus) tab.focus();
      });
      const reviews = name === 'reviews';
      this.q('[data-prv-panel="reviews"]').hidden = !reviews;
      const questions = this.q('[data-prv-panel="questions"]');
      if (questions) questions.hidden = reviews;
      this.q('[data-prv-filters]').hidden = !reviews || !this.online;
      this.q('[data-prv-mentions-panel]').hidden = !reviews || !this.online || !(Number(this.q('[data-prv-tab-count]').textContent) > 0);
      if (!reviews) this.q('[data-prv-note]').hidden = true;
      else this.renderNote(Number(this.q('[data-prv-tab-count]').textContent) || 0);
      if (!reviews && !this.questionsLoaded) this.loadQuestions();
    }

    /* ---------- static controls ---------- */

    bindStatic() {
      const score = this.q('[data-prv-score]');
      if (score) {
        score.addEventListener('click', () => {
          const hist = this.q('[data-prv-hist]');
          const open = score.getAttribute('aria-expanded') !== 'true';
          score.setAttribute('aria-expanded', String(open));
          hist.hidden = !open;
        });
      }
      this.q('[data-prv-rating]').addEventListener('change', (e) => this.setFilter({ rating: e.target.value }));
      this.q('[data-prv-sort]').addEventListener('change', (e) => this.setFilter({ sort: e.target.value }));
      const photos = this.q('[data-prv-photos]');
      photos.addEventListener('click', () => {
        const on = photos.getAttribute('aria-pressed') !== 'true';
        photos.setAttribute('aria-pressed', String(on));
        this.setFilter({ photos: on });
      });
      this.q('[data-prv-search]').addEventListener('submit', (e) => {
        e.preventDefault();
        this.setFilter({ q: this.q('[data-prv-q]').value.trim().slice(0, 60) });
      });
      const more = this.q('[data-prv-more]');
      more.addEventListener('click', () => {
        const chips = this.q('[data-prv-chips]');
        const expand = chips.classList.contains('is-collapsed');
        chips.classList.toggle('is-collapsed', !expand);
        more.setAttribute('aria-expanded', String(expand));
        more.textContent = expand ? 'View less' : 'View more';
      });
      const tabs = [...this.root.querySelectorAll('[data-prv-tab]')];
      tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.selectTab(tab.dataset.prvTab));
        tab.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          const visible = tabs.filter((t) => !t.hidden);
          const i = visible.indexOf(tab);
          const next = visible[(i + (e.key === 'ArrowRight' ? 1 : -1) + visible.length) % visible.length];
          if (next) { e.preventDefault(); this.selectTab(next.dataset.prvTab, true); }
        });
      });
      this.scope.querySelectorAll('[data-prv-open]').forEach((btn) => {
        btn.addEventListener('click', () => this.openDialog(btn.dataset.prvOpen, btn));
      });
      // Photo zoom: delegated, works for server-rendered and fetched thumbnails.
      this.root.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-prv-zoom]');
        if (!btn || !this.root.contains(btn)) return;
        const group = btn.closest('[data-prv-media-track], .prv-photos');
        const buttons = group ? [...group.querySelectorAll('[data-prv-zoom]')] : [btn];
        this.gallery = buttons.map((b) => b.dataset.full).filter(isHttps);
        this.galleryIndex = Math.max(0, buttons.indexOf(btn));
        this.lastTrigger = btn;
        this.showPhoto();
      });
    }

    /* ---------- dialogs ---------- */

    dialog(name) { return this.scope.querySelector(`[data-prv-dialog="${name}"]`); }

    open(dlg) {
      if (typeof dlg.showModal === 'function') dlg.showModal();
      else dlg.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';
    }

    close(dlg) {
      if (typeof dlg.close === 'function' && dlg.open) dlg.close();
      else dlg.removeAttribute('open');
    }

    openDialog(name, trigger) {
      const dlg = this.dialog(name);
      if (!dlg) return;
      this.lastTrigger = trigger || document.activeElement;
      const done = dlg.querySelector('[data-prv-done]');
      if (done && !done.hidden) this.resetForm(name);
      this.open(dlg);
      const first = dlg.querySelector(name === 'review' ? 'input[name="rating"]' : 'textarea, input');
      if (first) first.focus();
    }

    bindDialogs() {
      const all = [...this.scope.querySelectorAll('[data-prv-dialog], [data-prv-lightbox]')];
      all.forEach((dlg) => {
        dlg.querySelectorAll('[data-prv-close]').forEach((btn) => btn.addEventListener('click', () => this.close(dlg)));
        // Click on the backdrop (the dialog box itself, outside its content) closes it.
        dlg.addEventListener('click', (e) => { if (e.target === dlg) this.close(dlg); });
        dlg.addEventListener('close', () => {
          document.documentElement.style.overflow = '';
          if (this.lastTrigger && document.contains(this.lastTrigger)) this.lastTrigger.focus();
        });
      });
      this.bindReviewForm();
      this.bindQuestionForm();
      this.bindLightbox();
    }

    setError(field, msg) {
      if (!field) return;
      field.classList.toggle('is-invalid', !!msg);
      const err = field.querySelector('[data-prv-err]');
      if (err) err.textContent = msg || '';
    }

    setStatus(form, msg, isError) {
      const el = form.querySelector('[data-prv-status]');
      el.textContent = msg || '';
      el.classList.toggle('is-error', !!isError);
    }

    errorMessage(err) { return err && err.status === 429 ? MSG.busy : MSG.error; }

    resetForm(name) {
      const dlg = this.dialog(name);
      const form = dlg.querySelector('form');
      form.reset();
      form.querySelectorAll('[data-field]').forEach((f) => this.setError(f, ''));
      this.setStatus(form, '');
      dlg.querySelector('[data-prv-form-wrap]').hidden = false;
      dlg.querySelector('[data-prv-done]').hidden = true;
      if (name === 'review') { this.reviewFiles.forEach((f) => f.url && URL.revokeObjectURL(f.url)); this.reviewFiles = []; this.renderThumbs(); }
      this.requestIds[name] = uuid();
    }

    showDone(name) {
      const dlg = this.dialog(name);
      dlg.querySelector('[data-prv-form-wrap]').hidden = true;
      const done = dlg.querySelector('[data-prv-done]');
      done.hidden = false;
      done.focus();
    }

    /* review form */
    bindReviewForm() {
      this.requestIds = { review: uuid(), question: uuid() };
      this.reviewFiles = [];
      const dlg = this.dialog('review');
      if (!dlg) return;
      const form = dlg.querySelector('[data-prv-review-form]');
      const input = form.querySelector('[data-prv-files]');
      this.thumbs = form.querySelector('[data-prv-thumbs]');
      this.photoField = form.querySelector('[data-field="photos"]');
      input.addEventListener('change', () => { if (input.files.length) this.addFiles(input.files); input.value = ''; });
      form.addEventListener('submit', (e) => { e.preventDefault(); this.submitReview(form); });
    }

    addFiles(list) {
      if (this.savingReview) return;
      const rejected = [];
      Array.from(list).forEach((file) => {
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        // iOS sometimes reports HEIC with an empty type; fall back to the extension.
        const type = OK_TYPES.test(file.type) ? file.type.toLowerCase() : (!file.type || file.type === 'application/octet-stream') ? EXT_TYPES[ext] : '';
        if (!type) { rejected.push(`${file.name} (${MSG.fileType})`); return; }
        if (file.size > MAX_BYTES) { rejected.push(`${file.name} (${MSG.fileSize})`); return; }
        if (this.reviewFiles.some((f) => f.file.name === file.name && f.file.size === file.size && f.file.lastModified === file.lastModified)) return;
        if (this.reviewFiles.length >= MAX_PHOTOS) { if (!rejected.includes(MSG.tooMany)) rejected.push(MSG.tooMany); return; }
        this.reviewFiles.push({ file, type, url: /^image\/(jpeg|png|webp)$/.test(type) ? URL.createObjectURL(file) : '', resourceUrl: '' });
      });
      this.renderThumbs();
      this.setError(this.photoField, rejected.join(' · '));
    }

    renderThumbs() {
      if (!this.thumbs) return;
      this.thumbs.textContent = '';
      this.reviewFiles.forEach((f) => {
        const li = make('li', 'prv-thumb');
        if (f.url) {
          const img = make('img');
          img.src = f.url;
          img.alt = f.file.name;
          li.append(img);
        } else {
          li.append(document.createTextNode(f.file.name.slice(0, 14)));
        }
        const x = make('button');
        x.type = 'button';
        x.innerHTML = ICON.x;
        x.setAttribute('aria-label', `Remove ${f.file.name}`);
        x.disabled = !!this.savingReview;
        x.addEventListener('click', () => {
          if (this.savingReview) return;
          if (f.url) URL.revokeObjectURL(f.url);
          this.reviewFiles = this.reviewFiles.filter((g) => g !== f);
          this.renderThumbs();
        });
        li.append(x);
        this.thumbs.append(li);
      });
    }

    async uploadPhotos(form) {
      const pending = this.reviewFiles.filter((f) => !f.resourceUrl);
      if (!pending.length) return;
      const { targets } = await this.postJSON('/reviews/uploads', {
        request_id: this.requestIds.review,
        files: pending.map((f) => ({ type: f.type, size: f.file.size }))
      }, 20000);
      if (!Array.isArray(targets) || targets.length !== pending.length) throw new Error('targets');
      let done = this.reviewFiles.length - pending.length;
      const progress = () => this.setStatus(form, `${MSG.uploading} ${done} / ${this.reviewFiles.length}`);
      progress();
      let next = 0;
      const worker = async () => {
        while (next < pending.length) {
          const i = next++;
          const target = targets[i];
          const body = new FormData();
          target.parameters.forEach((p) => body.append(p.name, p.value));
          body.append('file', pending[i].file);
          const res = await fetch(target.url, { method: 'POST', body, signal: AbortSignal.timeout(120000) });
          if (!res.ok) throw new Error('upload ' + res.status);
          pending[i].resourceUrl = target.resourceUrl;
          done++;
          progress();
        }
      };
      await Promise.all([worker(), worker(), worker()]);
    }

    validate(form, fields) {
      let firstBad = null;
      fields.forEach(([name, test, msg]) => {
        const field = form.querySelector(`[data-field="${name}"]`);
        const ok = test();
        this.setError(field, ok ? '' : msg);
        if (!ok && !firstBad) firstBad = field;
      });
      if (firstBad) {
        const focusable = firstBad.querySelector('input, textarea');
        if (focusable) focusable.focus();
      }
      return !firstBad;
    }

    async submitReview(form) {
      if (this.savingReview) return;
      const val = (n) => (form.elements[n] && form.elements[n].value || '').trim();
      const rating = form.querySelector('input[name="rating"]:checked');
      const ok = this.validate(form, [
        ['rating', () => !!rating, MSG.rating],
        ['body', () => val('body').length >= 1, MSG.body],
        ['name', () => val('name').length >= 1, MSG.name],
        ['email', () => EMAIL.test(val('email')), MSG.email]
      ]);
      if (!ok) return;
      const button = form.querySelector('[data-prv-submit]');
      this.savingReview = true;
      button.disabled = true;
      this.renderThumbs();
      try {
        try { await this.uploadPhotos(form); } catch (e) {
          this.setStatus(form, 'Photo upload failed. Please try again or remove the photos.', true);
          return;
        }
        this.setStatus(form, MSG.saving);
        const result = await this.postJSON('/reviews/submit', {
          request_id: this.requestIds.review,
          product_id: this.productId,
          rating: Number(rating.value),
          title: val('title'),
          body: val('body'),
          name: val('name'),
          email: val('email').toLowerCase(),
          photos: this.reviewFiles.map((f) => f.resourceUrl),
          confirmation: form.elements.confirmation.value
        }, 45000);
        if (result.saved !== true) throw new Error('save');
        this.setStatus(form, '');
        this.showDone('review');
      } catch (e) {
        this.setStatus(form, this.errorMessage(e), true);
      } finally {
        this.savingReview = false;
        button.disabled = false;
        this.renderThumbs();
      }
    }

    /* question form */
    bindQuestionForm() {
      const dlg = this.dialog('question');
      if (!dlg) return;
      const form = dlg.querySelector('[data-prv-question-form]');
      form.addEventListener('submit', (e) => { e.preventDefault(); this.submitQuestion(form); });
    }

    async submitQuestion(form) {
      if (this.savingQuestion) return;
      const val = (n) => (form.elements[n] && form.elements[n].value || '').trim();
      const ok = this.validate(form, [
        ['question', () => val('question').length >= 5, MSG.question],
        ['name', () => val('name').length >= 1, MSG.name],
        ['email', () => EMAIL.test(val('email')), MSG.email]
      ]);
      if (!ok) return;
      const button = form.querySelector('[data-prv-submit]');
      this.savingQuestion = true;
      button.disabled = true;
      this.setStatus(form, MSG.saving);
      try {
        const payload = {
          request_id: this.requestIds.question,
          product_id: this.productId,
          question: val('question'),
          name: val('name'),
          email: val('email').toLowerCase(),
          confirmation: form.elements.confirmation.value
        };
        const result = await this.postJSON('/questions', payload, 20000);
        if (result.saved !== true) throw new Error('save');
        this.notifyStaff(payload);
        this.setStatus(form, '');
        this.showDone('question');
      } catch (e) {
        this.setStatus(form, this.errorMessage(e), true);
      } finally {
        this.savingQuestion = false;
        button.disabled = false;
      }
    }

    // Background post of the hidden native contact form: emails customer service. The question is
    // already saved in the backend, so a failure here (e.g. a CAPTCHA challenge) is not shown.
    notifyStaff(payload) {
      const native = this.root.querySelector('.prv-native-contact');
      if (!native) return;
      native.querySelector('[data-prv-c-request]').value = payload.request_id;
      native.querySelector('[data-prv-c-name]').value = payload.name;
      native.querySelector('[data-prv-c-email]').value = payload.email;
      native.querySelector('[data-prv-c-body]').value = 'Product question:\n' + payload.question;
      fetch(native.action, { method: 'POST', body: new FormData(native), credentials: 'same-origin' }).catch(() => {});
    }

    /* lightbox */
    bindLightbox() {
      const box = this.scope.querySelector('[data-prv-lightbox]');
      if (!box) return;
      this.lightbox = box;
      box.querySelector('[data-prv-lb-prev]').addEventListener('click', () => this.stepPhoto(-1));
      box.querySelector('[data-prv-lb-next]').addEventListener('click', () => this.stepPhoto(1));
      box.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.stepPhoto(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.stepPhoto(1); }
      });
    }

    showPhoto() {
      if (!this.lightbox || !this.gallery.length) return;
      const img = this.lightbox.querySelector('[data-prv-lb-img]');
      img.src = this.gallery[this.galleryIndex];
      img.alt = `Customer photo ${this.galleryIndex + 1} of ${this.gallery.length}`;
      this.lightbox.querySelector('[data-prv-lb-count]').textContent = this.gallery.length > 1 ? `${this.galleryIndex + 1} / ${this.gallery.length}` : '';
      const multi = this.gallery.length > 1;
      this.lightbox.querySelector('[data-prv-lb-prev]').hidden = !multi;
      this.lightbox.querySelector('[data-prv-lb-next]').hidden = !multi;
      if (!this.lightbox.open) {
        this.open(this.lightbox);
        this.lightbox.querySelector('[data-prv-close]').focus();
      }
    }

    stepPhoto(delta) {
      if (!this.gallery.length) return;
      this.galleryIndex = (this.galleryIndex + delta + this.gallery.length) % this.gallery.length;
      this.showPhoto();
    }
  }

  const init = (scope) => {
    (scope || document).querySelectorAll('[data-prv]').forEach((root) => {
      if (!root.__prv) root.__prv = new Reviews(root);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
